import { useState, Fragment, useEffect } from 'react';
import { trpc } from '@web/lib/trpc';
import { z } from 'zod';

import { toast } from 'sonner';

import { Copy, RefreshCw } from 'lucide-react';

import { Button } from '@web/components/ui/button';
import { Label } from '@web/components/ui/label';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@web/components/ui/accordion';

const allowedTypes = z.union([z.string(), z.number(), z.boolean()]);

export function ConversationAnalysis({ contactId }: { contactId: string }) {
  const {
    analyzeContactData,
    analyzeMedicalDetails,
    conversationFacts,
    conversationSentiment,
  } = trpc.messaging;

  return (
    <Accordion
      type="single"
      defaultValue="contact-info"
      collapsible
      className="mt-2 [&>*]:border-none"
    >
      <AccordionItem value="contact-info">
        <AccordionTrigger>
          <h3 className="ml-2 text-lg font-medium">Contact Info</h3>
        </AccordionTrigger>
        <AccordionContent>
          <Section
            contactId={contactId}
            name="contact info"
            route={analyzeContactData}
            fields={[
              ['firstName', 'First name'],
              ['lastName', 'Last name'],
              ['email', 'Email'],
              ['phoneNumber', 'Phone number'],
            ]}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="medical-details">
        <AccordionTrigger>
          <h3 className="ml-2 text-lg font-medium">Medical Details</h3>
        </AccordionTrigger>
        <AccordionContent>
          <Section
            contactId={contactId}
            name="medical details"
            route={analyzeMedicalDetails}
            fields={[
              ['alcohol', 'Alcohol'],
              ['smoke', 'Smoke'],
              ['medications', 'Medications'],
              ['allergies', 'Allergies'],
              ['height', 'Height'],
              ['weight', 'Weight'],
              ['diseases', 'Diseases'],
            ]}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="conversation-facts">
        <AccordionTrigger>
          <h3 className="ml-2 text-lg font-medium">Conversation Facts</h3>
        </AccordionTrigger>
        <AccordionContent>
          <Section
            contactId={contactId}
            name="conversation facts"
            route={conversationFacts}
            fields={[
              ['concernsByContact', 'Concerns by contact'],
              ['conversationPurpose', 'Conversation purpose'],
              ['cosmeticProcedures', 'Cosmetic procedures'],
              ['doctorNames', 'Doctor names'],
              ['importantDetails', 'Important details'],
              ['questionsByContact', 'Questions by contact'],
              ['requestsByContact', 'Requests by contact'],
              ['topicsDiscussed', 'Topics discussed'],
              ['doctorsMentionedByContact', 'Doctors mentioned by contact'],
              ['doctorsMentionedByRep', 'Doctors mentioned by representative'],
            ]}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="conversation-sentiment">
        <AccordionTrigger>
          <h3 className="ml-2 text-lg font-medium">Conversation Sentiment</h3>
        </AccordionTrigger>
        <AccordionContent>
          <Section
            contactId={contactId}
            name="conversation sentiment"
            route={conversationSentiment}
            fields={[
              ['contactSentiment', 'Contact sentiment'],
              ['contactSentimentScore', 'Contact sentiment score'],
              ['contactTone', 'Contact tone'],
              ['detailedSentiment', 'Detailed sentiment'],
              ['emotionalShift', 'Emotional shift'],
              [
                'observationsAboutRepCommunication',
                'Observations about representative communication',
              ],
              ['repSentiment', 'Representative sentiment'],
              ['repTone', 'Representative tone'],
              ['sentimentScore', 'Sentiment score'],
              ['suggestionsForRep', 'Suggestions for representative'],
              ['followUpActions', 'Follow up actions'],
            ]}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function Section<T>({
  contactId,
  route,
  fields,
}: {
  contactId: string;
  name: string;
  route: {
    useMutation: () => {
      mutateAsync: ({ contactId }: { contactId: string }) => Promise<T | null>;
    };
  };
  fields: [keyof T, string][];
}) {
  const { mutateAsync } = route.useMutation();
  const [data, setData] = useState<T | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    async function analyzeConversation() {
      if (data) return;
      setIsAnalyzing(true);

      const analysis = await mutateAsync({ contactId }).catch(
        (error: unknown) => {
          console.error(error);
          return null;
        },
      );

      if (analysis === null) {
        toast.error('Candy AI analysis failed', {
          description:
            'could not extract the requested data from the conversation.',
        });
      }

      setIsAnalyzing(false);
      setData(analysis);
    }

    void analyzeConversation();
  }, [data, mutateAsync, contactId]);

  return (
    <div className="relative">
      {isAnalyzing && (
        <div className="">
          <RefreshCw className="absolute bottom-0 left-0 right-0 top-0 m-auto h-4 w-4 animate-spin" />
        </div>
      )}

      <div className="mt-4">
        {data ? (
          fields.map(([key, label]) => {
            const rawValue = data[key];

            const atom = allowedTypes.safeParse(rawValue).data;
            const array = allowedTypes.array().safeParse(rawValue).data;

            const values = atom !== undefined ? [atom] : (array ?? []);

            const strValues = values.map((value) => {
              switch (typeof value) {
                case 'string':
                  return value;
                case 'number':
                  return value.toString();
                case 'boolean':
                  return value ? 'yes' : 'no';
              }
            });

            return (
              strValues.length > 0 && (
                <Fragment key={label}>
                  <div className="mt-4 flex flex-row flex-wrap justify-between gap-1 px-2 align-middle">
                    <Label className="w-max px-0 py-0 text-sm font-bold">
                      {label}
                    </Label>

                    {strValues.map((value) => (
                      <Button
                        key={value}
                        variant="ghost"
                        className="group flex h-min items-center justify-start gap-1 justify-self-start whitespace-normal px-0 py-0 text-start text-sm"
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(value.toString())
                            .then(() =>
                              toast.success(`copied: ${value.toString()}`),
                            );
                        }}
                      >
                        {value}
                        <Copy className="invisible ml-2 h-4 w-4 shrink-0 group-hover:visible" />
                      </Button>
                    ))}
                  </div>
                </Fragment>
              )
            );
          })
        ) : (
          <div className="space-y-4">
            {fields.map(([, label]) => (
              <div key={label} className="flex flex-row justify-between px-2">
                <div className="bg-muted h-5 w-24 animate-pulse rounded" />
                <div className="bg-muted h-5 w-48 animate-pulse rounded" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
